import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabaseClient';
import { invitationService } from '../../services/invitationService';
import OAuthButtons from '../../components/auth/OAuthButtons';
import { validatePassword, PASSWORD_HELPER_TEXT } from '../../utils/passwordValidation';
import PhoneInput from '../../components/common/PhoneInput';
import { sanitizeNameInput } from '../../utils/nameInput';

const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('+56');
  const [collaboratingLawyersCount, setCollaboratingLawyersCount] = useState<number>(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showOrganizationDialog, setShowOrganizationDialog] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [creatingOrganization, setCreatingOrganization] = useState(false);
  
  const { signUp, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  useTranslation();

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const validateForm = (): boolean => {
    if (!email || !password || !confirmPassword || !firstName || !lastName || !phoneNumber) {
      setError('Todos los campos son requeridos');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('El email no es válido');
      return false;
    }

    // Password validation
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return false;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }

    // Validate phone number with country code format
    const phoneWithoutCode = phoneNumber.replace(/^\+\d{1,4}\s?/, '').trim();
    if (!phoneWithoutCode) {
      setError('El número de teléfono es requerido');
      return false;
    }
    const phoneRegex = /^\+\d{1,4}\s[\d\s-]{7,15}$/;
    if (!phoneRegex.test(phoneNumber.trim())) {
      setError('El número de teléfono no es válido');
      return false;
    }

    if (collaboratingLawyersCount < 0 || !Number.isInteger(collaboratingLawyersCount)) {
      setError('El número de abogados colaboradores debe ser un número entero mayor o igual a 0');
      return false;
    }

    if (!termsAccepted) {
      setError('Debes aceptar la Política de Privacidad y los Términos de Servicio para continuar');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Sign up user
      const { data: authData, error: signUpError } = await signUp(email, password, {
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        collaborating_lawyers_count: collaboratingLawyersCount,
      });

      if (signUpError) {
        throw signUpError;
      }

      // Update profile with phone number, collaboration count, and consent
      if (authData?.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            phone_number: phoneNumber,
            collaborating_lawyers_count: collaboratingLawyersCount,
            marketing_consent: marketingConsent,
            analytics_consent: analyticsConsent,
            consent_version: '2.0',
            consent_timestamp: new Date().toISOString(),
            terms_accepted_version: '2.0',
            terms_accepted_at: new Date().toISOString(),
          })
          .eq('id', authData.user.id);

        if (profileError) {
          console.error('Error updating profile:', profileError);
          // Don't throw - profile update is not critical for signup
        }

        // Record consent in consent_history for audit trail
        // Note: consent_history is a new table from migration 004. Using rpc to bypass
        // strict type checking until Supabase types are regenerated.
        const consentRecords = [
          { user_id: authData.user.id, consent_type: 'terms', consent_given: true, consent_version: '2.0' },
          { user_id: authData.user.id, consent_type: 'privacy_policy', consent_given: true, consent_version: '2.0' },
          { user_id: authData.user.id, consent_type: 'marketing', consent_given: marketingConsent, consent_version: '2.0' },
          { user_id: authData.user.id, consent_type: 'analytics', consent_given: analyticsConsent, consent_version: '2.0' },
        ];

        for (const record of consentRecords) {
          const { error: consentError } = await supabase.rpc('record_consent_change' as any, {
            p_user_id: record.user_id,
            p_consent_type: record.consent_type,
            p_consent_given: record.consent_given,
            p_consent_version: record.consent_version,
          });

          if (consentError) {
            console.error('Error recording consent:', consentError);
            // Don't throw - consent recording is not critical for signup
          }
        }

        // Check if user collaborates with others
        if (collaboratingLawyersCount > 0) {
          // Show dialog to create organization
          setShowOrganizationDialog(true);
          setIsLoading(false);
          return;
        }
      }

      setSuccess(true);
    } catch (err: any) {
      console.error('Sign up error:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Error al crear la cuenta');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!organizationName.trim()) {
      setError('El nombre de la organización es requerido');
      return;
    }

    setCreatingOrganization(true);
    setError('');

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuario no autenticado');
      }

      // Create organization
      const slug = organizationName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: organizationName,
          slug,
          subscription_tier: 'basic',
          subscription_status: 'active',
          max_users: 5,
          max_storage_mb: 5120,
          max_cases: 50,
          is_active: true,
        })
        .select()
        .single();

      if (orgError || !orgData) {
        throw orgError || new Error('Error al crear la organización');
      }

      // Update user profile with organization and set as admin
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          organization_id: orgData.id,
          role: 'admin',
        })
        .eq('id', user.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        // Rollback organization
        await supabase.from('organizations').delete().eq('id', orgData.id);
        throw profileError;
      }

      // Initialize invitation credits based on collaboration count
      // Minimum: collaborating_lawyers_count + 1 (for buffer)
      const credits = Math.max(collaboratingLawyersCount + 1, 1);
      await invitationService.initializeInvitationCredits(orgData.id, credits);

      setShowOrganizationDialog(false);
      setSuccess(true);
      // Redirect to invitations page after a short delay
      setTimeout(() => {
        navigate('/admin/subscription', { replace: true });
      }, 2000);
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Error al crear la organización');
    } finally {
      setCreatingOrganization(false);
    }
  };

  const handleSkipOrganization = () => {
    setShowOrganizationDialog(false);
    setSuccess(true);
    // Redirect to dashboard after a short delay
    setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 2000);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Ston
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Crea una cuenta para comenzar
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* OAuth Buttons */}
          <OAuthButtons mode="signup" onError={setError} />

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstName"
              label="Nombre"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(sanitizeNameInput(e.target.value))}
              disabled={isLoading || success}
              inputProps={{ maxLength: 15 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="lastName"
              label="Apellido"
              name="lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(sanitizeNameInput(e.target.value))}
              disabled={isLoading || success}
              inputProps={{ maxLength: 15 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading || success}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || success}
              helperText={PASSWORD_HELPER_TEXT}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirmar Contraseña"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading || success}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                label="Teléfono"
                required
                disabled={isLoading || success}
                name="phoneNumber"
                autoComplete="tel"
              />
            </Box>
            <TextField
              margin="normal"
              required
              fullWidth
              id="collaboratingLawyersCount"
              label="¿Con cuántos abogados colaboras?"
              name="collaboratingLawyersCount"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={collaboratingLawyersCount}
              onChange={(e) => setCollaboratingLawyersCount(parseInt(e.target.value) || 0)}
              disabled={isLoading || success}
              helperText="Si trabajas solo, ingresa 0"
            />
            {/* GDPR Consent Checkboxes */}
            <FormGroup sx={{ mt: 3, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={isLoading || success}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    He leído y acepto la{' '}
                    <MuiLink href="/privacy" target="_blank">
                      Política de Privacidad
                    </MuiLink>{' '}
                    y los{' '}
                    <MuiLink href="/terms" target="_blank">
                      Términos de Servicio
                    </MuiLink>{' '}
                    <Typography component="span" variant="body2" color="error">*</Typography>
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    disabled={isLoading || success}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Acepto recibir comunicaciones de marketing (opcional)
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={analyticsConsent}
                    onChange={(e) => setAnalyticsConsent(e.target.checked)}
                    disabled={isLoading || success}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Acepto el uso de cookies analíticas para mejorar el servicio (opcional)
                  </Typography>
                }
              />
            </FormGroup>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2 }}
              disabled={isLoading || success}
            >
              {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
            </Button>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              ¿Ya tienes una cuenta?{' '}
              <MuiLink component={Link} to="/login" sx={{ textDecoration: 'none' }}>
                Inicia sesión
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Organization Creation Dialog */}
      <Dialog
        open={showOrganizationDialog}
        onClose={handleSkipOrganization}
        aria-labelledby="organization-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="organization-dialog-title">
          Crear Organización
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="organization-dialog-description" sx={{ mb: 2 }}>
            Parece que trabajas en equipo. ¿Deseas crear una organización para invitar a tus colegas?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la Organización"
            fullWidth
            variant="outlined"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            disabled={creatingOrganization}
            helperText="Este será el nombre de tu estudio u organización"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSkipOrganization}
            disabled={creatingOrganization}
          >
            Omitir
          </Button>
          <Button
            onClick={handleCreateOrganization}
            variant="contained"
            disabled={creatingOrganization || !organizationName.trim()}
          >
            {creatingOrganization ? 'Creando...' : 'Crear Organización'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Verification Modal */}
      <Dialog
        open={success && !showOrganizationDialog}
        onClose={() => {
          navigate('/login', { replace: true });
        }}
        aria-labelledby="verification-dialog-title"
        aria-describedby="verification-dialog-description"
      >
        <DialogTitle id="verification-dialog-title">
          Cuenta creada exitosamente
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="verification-dialog-description">
            Te hemos enviado un correo electrónico a <strong>{email}</strong> para verificar tu cuenta.
            Por favor, revisa tu bandeja de entrada y haz clic en el enlace de verificación para activar tu cuenta.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              navigate('/login', { replace: true });
            }}
            variant="contained"
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignUp;
